import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Apirequests, Apirequests2 } from "../../utils/ApiRequests";

const API_URL = process.env.REACT_APP_IMAGE_URI || "";

const initialForm = {
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    language: "English",
    price: "",
    ratingAverage: "",
    ratingCount: "",
    status: "active",
    pageLink: ""
};

const WorkshopForm = ({ workshop, close, refresh }) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const isEdit = workshop !== null;
    const [form, setForm] = useState(initialForm);
    const [imageMode, setImageMode] = useState("url");
    const [thumbnailUrl, setThumbnailUrl] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [dragActive, setDragActive] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // ----------------------------------
    // Convert Mongo date → datetime-local
    // ----------------------------------
    const formatDateForInput = (date) => {
        if (!date) return "";

        const d = new Date(date);

        const offset = d.getTimezoneOffset();
        const localDate = new Date(
            d.getTime() - offset * 60 * 1000
        );

        return localDate.toISOString().slice(0, 16);
    };

    // ----------------------------------
    // Resolve existing thumbnail
    // ----------------------------------
    const getImageUrl = (thumbnail) => {
        if (!thumbnail) return "";

        if (
            thumbnail.startsWith("http://") ||
            thumbnail.startsWith("https://")
        ) {
            return thumbnail;
        }

        return `${API_URL}${thumbnail}`;
    };

    // ----------------------------------
    // Fetch workshop for edit
    // ----------------------------------
    useEffect(() => {
        if (!isEdit || !workshop) return;


        setForm({
            title: workshop.title || "",
            description: workshop.description || "",
            startDate: formatDateForInput(workshop.startDate),
            endDate: formatDateForInput(workshop.endDate),
            language: workshop.language || "English",
            price: workshop.price ?? "",
            ratingAverage: workshop.rating?.average ?? "",
            ratingCount: workshop.rating?.count ?? "",
            status: workshop.status || "active",
            pageLink: workshop.pageLink || "",
        });

        if (
            workshop.thumbnail?.startsWith("http://") ||
            workshop.thumbnail?.startsWith("https://")
        ) {
            setImageMode("url");
            setThumbnailUrl(workshop.thumbnail);
        } else {
            setImageMode("upload");
        }

        setImagePreview(
            getImageUrl(workshop.thumbnail)
        );
    }, [workshop, isEdit]);

    // ----------------------------------
    // Normal input change
    // ----------------------------------
    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // ----------------------------------
    // File handling
    // ----------------------------------
    const handleFile = (file) => {
        if (!file) return;

        const allowed = [
            "image/jpeg",
            "image/png",
            "image/webp",
        ];

        if (!allowed.includes(file.type)) {
            setError(
                "Only JPG, PNG and WEBP images are allowed"
            );
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError("Image must be less than 5MB");
            return;
        }

        setError("");
        setImageFile(file);

        const preview = URL.createObjectURL(file);
        setImagePreview(preview);
    };

    const handleFileChange = (e) => {
        handleFile(e.target.files?.[0]);
    };

    // ----------------------------------
    // Drag & drop
    // ----------------------------------
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];

        handleFile(file);
    };

    // ----------------------------------
    // Image mode
    // ----------------------------------
    const changeImageMode = (mode) => {
        setImageMode(mode);
        setError("");

        if (mode === "url") {
            setImageFile(null);

            if (thumbnailUrl) {
                setImagePreview(thumbnailUrl);
            } else if (!isEdit) {
                setImagePreview("");
            }
        }

        if (mode === "upload") {
            if (imageFile) {
                setImagePreview(
                    URL.createObjectURL(imageFile)
                );
            } else if (!isEdit) {
                setImagePreview("");
            }
        }
    };

    const handleUrlChange = (e) => {
        const value = e.target.value;

        setThumbnailUrl(value);
        setImagePreview(value);
    };

    // ----------------------------------
    // Submit
    // ----------------------------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");

        if (!form.title.trim()) {
            return setError("Title is required");
        }

        if (!form.startDate || !form.endDate) {
            return setError(
                "Start date and end date are required"
            );
        }

        if (
            new Date(form.endDate) <
            new Date(form.startDate)
        ) {
            return setError(
                "End date cannot be before start date"
            );
        }

        if (
            !isEdit &&
            imageMode === "url" &&
            !thumbnailUrl.trim()
        ) {
            return setError("Image URL is required");
        }

        if (
            !isEdit &&
            imageMode === "upload" &&
            !imageFile
        ) {
            return setError("Please upload an image");
        }

        if (
            form.ratingAverage !== "" &&
            (
                Number(form.ratingAverage) < 0 ||
                Number(form.ratingAverage) > 5
            )
        ) {
            return setError(
                "Rating must be between 0 and 5"
            );
        }

        if (
            form.ratingCount !== "" &&
            Number(form.ratingCount) < 0
        ) {
            return setError(
                "Review count cannot be negative"
            );
        }

        if (!form.pageLink) {
            return setError(
                "Page Link cannot be empty"
            );
        }

        try {
            setSubmitting(true);

            const token =
                localStorage.getItem("token");

            if (!token) {
                return setError("Login token not found");
            }

            const formData = new FormData();

            formData.append(
                "title",
                form.title.trim()
            );

            formData.append(
                "description",
                form.description
            );

            formData.append(
                "startDate",
                form.startDate
            );

            formData.append(
                "endDate",
                form.endDate
            );

            formData.append(
                "language",
                form.language
            );

            formData.append(
                "price",
                form.price
            );

            formData.append(
                "ratingAverage",
                form.ratingAverage || 0
            );

            formData.append(
                "ratingCount",
                form.ratingCount || 0
            );

            formData.append(
                "status",
                form.status
            );

            formData.append(
                "pageLink",
                form.pageLink
            );

            formData.append(
                "imageMode",
                imageMode
            );

            // URL IMAGE
            if (
                imageMode === "url" &&
                thumbnailUrl.trim()
            ) {
                formData.append(
                    "thumbnailUrl",
                    thumbnailUrl.trim()
                );
            }

            // UPLOADED IMAGE
            if (
                imageMode === "upload" &&
                imageFile
            ) {
                formData.append(
                    "thumbnail",
                    imageFile
                );
            }


            // ONLY Authorization header
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            let resp;

            if (isEdit) {
                resp = await Apirequests2(
                    `workshops/edit/${workshop?._id}`,
                    "PUT",
                    formData,
                    headers
                );
            } else {
                resp = await Apirequests2(
                    `workshops/create`,
                    "POST",
                    formData,
                    headers
                );
            }

            if (resp?.success) {
                await refresh();
                await close()
            } else {
                setError(
                    resp?.message ||
                    `Failed to ${isEdit
                        ? "update"
                        : "create"
                    } workshop`
                );
            }
        } catch (err) {
            console.error("Workshop error:", err);

            setError(
                err?.response?.data?.message ||
                `Failed to ${isEdit
                    ? "update"
                    : "create"
                } workshop`
            );
        } finally {
            setSubmitting(false);
        }
    };

    // ----------------------------------
    // Loading
    // ----------------------------------
    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-gray-800" />
            </div>
        );
    }

    return (
        <div className="">
            <div className="mx-auto max-w-5xl">

                {/* Header */}
                <div className="mb-6">


                    <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
                        {isEdit
                            ? "Edit Workshop"
                            : "Add Workshop"}
                    </h1>

                    <p className="mt-1 text-sm text-gray-500">
                        {isEdit
                            ? "Update workshop information"
                            : "Create a new workshop"}
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                    <div className="space-y-7 p-5 sm:p-7">

                        {/* Error */}
                        {error && (
                            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                            </div>
                        )}

                        {/* Basic Details */}
                        <div>
                            <h2 className="mb-4 text-base font-semibold text-gray-900">
                                Workshop Details
                            </h2>

                            <div className="grid gap-5 md:grid-cols-2">

                                {/* Title */}
                                <div className="md:col-span-2">
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Workshop Title
                                    </label>

                                    <input
                                        type="text"
                                        name="title"
                                        value={form.title}
                                        onChange={handleChange}
                                        placeholder="e.g. Bread & Sandwiches Workshop"
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />
                                </div>

                                {/* Description */}
                                <div className="md:col-span-2">
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Description
                                    </label>

                                    <textarea
                                        name="description"
                                        rows="4"
                                        maxLength="250"
                                        value={form.description}
                                        onChange={handleChange}
                                        placeholder="Short description shown on workshop card..."
                                        className="w-full resize-none rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />

                                    <div className="mt-1 text-right text-xs text-gray-400">
                                        {form.description.length}/250
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Workshop Page Link
                                    </label>

                                    <input
                                        type="text"
                                        name="pageLink"
                                        value={form.pageLink}
                                        onChange={handleChange}
                                        placeholder="e.g. Bread & Sandwiches Workshop"
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />
                                </div>

                                {/* Start */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Start Date
                                    </label>

                                    <input
                                        type="datetime-local"
                                        name="startDate"
                                        value={form.startDate}
                                        onChange={handleChange}
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />
                                </div>

                                {/* End */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        End Date
                                    </label>

                                    <input
                                        type="datetime-local"
                                        name="endDate"
                                        value={form.endDate}
                                        onChange={handleChange}
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />
                                </div>

                                {/* Language */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Language
                                    </label>

                                    <select
                                        name="language"
                                        value={form.language}
                                        onChange={handleChange}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    >
                                        <option value="English">
                                            English
                                        </option>
                                        <option value="Hindi">
                                            Hindi
                                        </option>
                                        <option value="English & Hindi">
                                            English & Hindi
                                        </option>
                                    </select>
                                </div>

                                {/* Price */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Price (₹)
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        name="price"
                                        value={form.price}
                                        onChange={handleChange}
                                        placeholder="590"
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />
                                </div>

                                {/* Rating */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Rating
                                    </label>

                                    <input
                                        type="number"
                                        name="ratingAverage"
                                        value={form.ratingAverage}
                                        onChange={handleChange}
                                        min="0"
                                        max="5"
                                        step="0.1"
                                        placeholder="4.7"
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />

                                    <p className="mt-1 text-xs text-gray-400">
                                        Between 0 and 5
                                    </p>
                                </div>

                                {/* Review Count */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Number of Reviews
                                    </label>

                                    <input
                                        type="number"
                                        name="ratingCount"
                                        value={form.ratingCount}
                                        onChange={handleChange}
                                        min="0"
                                        step="1"
                                        placeholder="147"
                                        className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    />
                                </div>
                                {/* Status */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Status
                                    </label>

                                    <select
                                        name="status"
                                        value={form.status}
                                        onChange={handleChange}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                    >
                                        <option value="active">
                                            Active
                                        </option>

                                        <option value="inactive">
                                            Inactive
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Image */}
                        <div>
                            <div className="mb-4">
                                <h2 className="text-base font-semibold text-gray-900">
                                    Workshop Image
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    Use an image URL or upload an
                                    image from your computer.
                                </p>
                            </div>

                            {/* Toggle */}
                            <div className="mb-5 inline-flex rounded-lg bg-gray-100 p-1">
                                <button
                                    type="button"
                                    onClick={() =>
                                        changeImageMode("url")
                                    }
                                    className={`rounded-md px-4 py-2 text-sm font-medium transition ${imageMode === "url"
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500"
                                        }`}
                                >
                                    Image URL
                                </button>

                                <button
                                    type="button"
                                    onClick={() =>
                                        changeImageMode("upload")
                                    }
                                    className={`rounded-md px-4 py-2 text-sm font-medium transition ${imageMode === "upload"
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500"
                                        }`}
                                >
                                    Upload Image
                                </button>
                            </div>

                            <div className="">

                                {/* Input */}
                                <div>
                                    {imageMode === "url" ? (
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                                Image URL
                                            </label>

                                            <input
                                                type="url"
                                                value={thumbnailUrl}
                                                onChange={handleUrlChange}
                                                placeholder="https://example.com/workshop.jpg"
                                                className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                            className={`flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition ${dragActive
                                                ? "border-gray-700 bg-gray-50"
                                                : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                                                }`}
                                        >
                                            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100">
                                                <svg
                                                    className="h-5 w-5 text-gray-600"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M4 16.5V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.5M12 3v13m0-13-4 4m4-4 4 4"
                                                    />
                                                </svg>
                                            </div>

                                            <p className="text-sm font-medium text-gray-700">
                                                Drag & drop image here
                                            </p>

                                            <p className="mt-1 text-xs text-gray-400">
                                                or click to browse
                                            </p>

                                            <p className="mt-3 text-xs text-gray-400">
                                                JPG, PNG or WEBP · Max 5MB
                                            </p>

                                            {imageFile && (
                                                <p className="mt-3 max-w-full truncate text-sm font-medium text-gray-700">
                                                    {imageFile.name}
                                                </p>
                                            )}

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={handleFileChange}
                                                className="hidden"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Preview */}
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                        Preview
                                    </label>

                                    <div className="flex min-h-[220px] items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                                        {imagePreview ? (
                                            <img
                                                src={imagePreview}
                                                alt="Workshop preview"
                                                className="h-[220px] w-full object-cover"
                                                onError={(e) => {
                                                    e.currentTarget.style.display =
                                                        "none";
                                                }}
                                            />
                                        ) : (
                                            <div className="text-center text-sm text-gray-400">
                                                No image selected
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
                        <button
                            type="button"
                            disabled={submitting}
                            onClick={() =>
                                navigate("/admin/workshops")
                            }
                            className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting
                                ? isEdit
                                    ? "Updating..."
                                    : "Creating..."
                                : isEdit
                                    ? "Update Workshop"
                                    : "Create Workshop"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WorkshopForm;